import { PetParentAnimal } from '../../../types/PetParent';

export type AlbumSpecies = 'cat' | 'dog';

export interface PromptTemplate {
  title: string;
  description: string;
}

export const WELCOME_HOME_ALBUM_TITLE = 'Welcome Home';
export const WELCOME_HOME_ALBUM_DESCRIPTION = "Celebrate [pet name]'s arrival and those first sweet moments together.";

const CAT_PROMPTS: PromptTemplate[] = [
  {
    title: 'Introducing [pet name]',
    description: 'Snap a photo of [pet name] and share the story behind how they got their name.',
  },
  {
    title: 'Welcome Home Look',
    description: "Show [pet name]'s face when they truly looked at home.",
  },
  {
    title: 'First Nap',
    description: 'Capture where [pet name] chose to nap in your home.',
  },
  {
    title: 'Morning Routine',
    description: 'How do you and [pet name] start the day? Snap a photo of your morning routine together.',
  },
  {
    title: 'Building Trust',
    description: 'When did [pet name] first show they trusted you? Capture that special moment in a photo.',
  },
  {
    title: 'First Toy',
    description: 'What toy did [pet name] choose first? Share a photo of them enjoying it.',
  },
  {
    title: 'First Window Watch',
    description: 'Capture [pet name] perched by a window or watching the world outside for the first time.',
  },
  {
    title: 'First Climb',
    description: 'Show [pet name] exploring high places - like a cat tree, shelf, or windowsill.',
  },
  {
    title: 'First Outdoor View',
    description: 'Share a photo of [pet name] safely exploring the outdoors from a window, catio, or harness.',
  },
  {
    title: 'First Friends',
    description: "Share a photo of [pet name] with someone they've bonded with - animal or human.",
  },
  {
    title: 'First Family Photo',
    description: 'Share a photo of [pet name] with family - whether people or other pets.',
  },
  {
    title: 'Silly Habits',
    description: "What's [pet name]'s quirkiest habit? Capture it in a photo that makes you smile.",
  },
  {
    title: 'First Days Together',
    description: 'What did those first few days feel like? Share a photo that captures your new life together.',
  },
];

const DOG_PROMPTS: PromptTemplate[] = [
  {
    title: 'Introducing [pet name]',
    description: 'Snap a photo of [pet name] and share the story behind how they got their name.',
  },
  {
    title: 'Welcome Home Look',
    description: "Show [pet name]'s face when they truly looked at home.",
  },
  {
    title: 'First Nap',
    description: 'Capture where [pet name] chose to nap or feel safe in your home.',
  },
  {
    title: 'Morning Routine',
    description: 'How do you and [pet name] start the day? Snap a photo of your morning routine together.',
  },
  {
    title: 'Building Trust',
    description: 'When did [pet name] first show they trusted you? Capture that special moment in a photo.',
  },
  {
    title: 'First Toy',
    description: 'What toy did [pet name] choose first? Share a photo of them enjoying it.',
  },
  {
    title: 'First Walk Together',
    description: 'Capture a photo from your first walk or outing with [pet name] - a favorite route, park, or trail.',
  },
  {
    title: 'First Outdoor Adventure',
    description: 'Capture [pet name] exploring the yard or outdoor space for the first time.',
  },
  {
    title: 'First Outing',
    description: 'Where did you take [pet name] on your first adventure together? Share a photo from that day.',
  },
  {
    title: 'First Friends',
    description: "Share a photo of [pet name] with someone they've bonded with - animal or human.",
  },
  {
    title: 'First Family Photo',
    description: 'Share a photo of [pet name] with family - whether people or other pets.',
  },
  {
    title: 'Silly Habits',
    description: "What's [pet name]'s quirkiest habit? Capture it in a photo that makes you smile.",
  },
  {
    title: 'First Days Together',
    description: 'What did those first few days feel like? Share a photo that captures your new life together.',
  },
];

export const getAlbumSpecies = (pet?: Pick<PetParentAnimal, 'animalType'> | null): AlbumSpecies => (
  pet?.animalType?.toLowerCase() === 'cat' ? 'cat' : 'dog'
);

export const getWelcomeHomePromptsForSpecies = (
  species: AlbumSpecies,
): PromptTemplate[] => (species === 'cat' ? CAT_PROMPTS : DOG_PROMPTS);
